/**
 * Feedback (DS foundation): Modal and Drawer (the shared base-ui Dialog), Banner, ErrorState and InlineError.
 * Confirmation is ConfirmDialog / useConfirm (controls.tsx); toasts are `notify` (toast.tsx). Nothing here
 * ever calls window.alert / confirm / prompt.
 */
import type { ReactNode } from "react";
import { Button, Dialog, type DialogProps } from "./components.js";

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");

/** Centered modal dialog: focus trap, Escape, outside press, scroll lock, focus return. Put decisions in `footer`. */
export function Modal(props: Omit<DialogProps, "variant">) {
  return <Dialog {...props} variant="modal" />;
}

/** Side drawer from the trailing edge (full width on phones). Same contract as Modal; body scrolls, footer docks. */
export function Drawer(props: Omit<DialogProps, "variant" | "totals">) {
  return <Dialog {...props} variant="drawer" />;
}

export type BannerTone = "info" | "success" | "warning" | "danger";

function ToneIcon({ tone }: { tone: BannerTone }) {
  return (
    <svg className="ffx-banner__icon" viewBox="0 0 16 16" aria-hidden="true">
      {tone === "success" ? (
        <>
          <circle cx="8" cy="8" r="6.25" />
          <path d="m5.25 8.25 1.9 1.9 3.6-4" />
        </>
      ) : tone === "info" ? (
        <>
          <circle cx="8" cy="8" r="6.25" />
          <path d="M8 7.25v3.75" />
          <circle cx="8" cy="5" r="0.4" fill="currentColor" />
        </>
      ) : (
        <>
          <path d="M8 2.25 14.25 13.5H1.75L8 2.25Z" />
          <path d="M8 6.5v3" />
          <circle cx="8" cy="11.4" r="0.4" fill="currentColor" />
        </>
      )}
    </svg>
  );
}

export type BannerProps = {
  tone?: BannerTone;
  title?: ReactNode;
  children?: ReactNode;
  /** Buttons or links at the end (one primary at most). */
  actions?: ReactNode;
  /** Shows a close button. */
  onDismiss?: () => void;
  className?: string;
};

/**
 * Page- or section-level message. Tone sets the stripe, icon and role: danger is an alert (announced at once),
 * the others a polite status. Text carries the meaning; color and icon only reinforce it.
 */
export function Banner({ tone = "info", title, children, actions, onDismiss, className }: BannerProps) {
  return (
    <div className={cx("ffx-banner", `ffx-banner--${tone}`, className)} role={tone === "danger" ? "alert" : "status"}>
      <ToneIcon tone={tone} />
      <div className="ffx-banner__text">
        {title ? <p className="ffx-banner__title">{title}</p> : null}
        {children ? <div className="ffx-banner__body">{children}</div> : null}
      </div>
      {actions ? <div className="ffx-banner__actions">{actions}</div> : null}
      {onDismiss ? (
        <button type="button" className="ffx-dialog__close ffx-banner__close" aria-label="Dismiss" onClick={onDismiss}>
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      ) : null}
    </div>
  );
}

/**
 * A region that failed to load (a table, a tab, a card): what failed, why if known, and a retry. Replaces the
 * content it stands for, at the same place, so the page never jumps to a toast to explain a missing section.
 */
export function ErrorState({ title = "Couldn't load this", message, onRetry, retryLabel = "Try again", compact = false }: { title?: string; message?: ReactNode; onRetry?: () => void; retryLabel?: string; compact?: boolean }) {
  return (
    <div className={cx("ffx-errstate", compact && "ffx-errstate--compact")} role="alert">
      <ToneIcon tone="danger" />
      <div className="ffx-errstate__text">
        <p className="ffx-errstate__title">{title}</p>
        {message ? <p className="ffx-errstate__msg">{message}</p> : null}
      </div>
      {onRetry ? (
        <Button size="sm" onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}

/** One-line error next to the thing it is about (a row action that failed, a section save). Polite live region. */
export function InlineError({ children, id }: { children?: ReactNode; id?: string }) {
  return (
    <p className="ffx-inline-error" id={id} aria-live="polite">
      {children ? (
        <>
          <ToneIcon tone="danger" />
          <span>{children}</span>
        </>
      ) : null}
    </p>
  );
}
